import { createTheme } from '@mui/material/styles';
import type { ThemeOptions, Theme } from '@mui/material/styles';

// Accent color type - extended to match Zoho theme options
type AccentColor = 'blue' | 'green' | 'yellow' | 'red' | 'pink' | 'gray' | 'teal' | 'purple' | 'orange';

// Accent color palette mapping
const accentColorPalette: Record<AccentColor, { main: string; light: string; dark: string }> = {
  blue: { main: '#1a73e8', light: '#4285f4', dark: '#1557b0' },
  green: { main: '#34a853', light: '#5bb974', dark: '#1e8e3e' },
  yellow: { main: '#f9ab00', light: '#fdd835', dark: '#f57c00' },
  red: { main: '#ea4335', light: '#f28b82', dark: '#c5221f' },
  pink: { main: '#e91e63', light: '#f48fb1', dark: '#c2185b' },
  gray: { main: '#455a64', light: '#78909c', dark: '#37474f' },
  teal: { main: '#00bcd4', light: '#4dd0e1', dark: '#0097a7' },
  purple: { main: '#9c27b0', light: '#ba68c8', dark: '#7b1fa2' },
  orange: { main: '#ff5722', light: '#ff8a65', dark: '#e64a19' },
};

// Get primary color based on accent color
const getPrimaryColor = (accentColor: AccentColor = 'blue') => {
  const accent = accentColorPalette[accentColor] || accentColorPalette.blue;
  return {
    main: accent.main,
    light: accent.light,
    dark: accent.dark,
    contrastText: '#fff',
  };
};

// Inter - modern UI font (same as admin panel, comparable to Outlook/Zoho)
const FONT_FAMILY = '"Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';

// Shared typography configuration - Zoho Mail style
const sharedTypography = {
  fontFamily: FONT_FAMILY,
  fontSize: 14,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em' },
  h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.35 },
  h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
  h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
  h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
  subtitle1: { fontSize: '0.9375rem', fontWeight: 400, lineHeight: 1.5 },
  subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
  body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
  body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.55 },
  caption: { fontSize: '0.75rem', fontWeight: 400, color: '#666' },
  overline: { fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em' },
  button: { textTransform: 'none' as const, fontWeight: 600, fontSize: '0.875rem' },
};

// Shared shape configuration
const sharedShape = {
  borderRadius: 3,
};

// Shared components configuration (light mode)
const sharedLightComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        fontFamily: `${FONT_FAMILY} !important`,
      },
      body: {
        fontFamily: `${FONT_FAMILY} !important`,
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: {
        fontFamily: `${FONT_FAMILY} !important`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        padding: '6px 16px',
        boxShadow: 'none',
        fontFamily: `${FONT_FAMILY} !important`,
        '&:hover': { boxShadow: 'none' },
      },
      contained: {
        '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontFamily: `${FONT_FAMILY} !important`,
      },
      secondary: {
        fontFamily: `${FONT_FAMILY} !important`,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: { '& .MuiOutlinedInput-root': { borderRadius: 3 } },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { boxShadow: '0 1px 2px rgba(0,0,0,0.06)' },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        margin: '1px 4px',
        '&.Mui-selected': {
          backgroundColor: '#e8f0fe',
          '&:hover': { backgroundColor: '#d2e3fc' },
        },
      },
    },
  },
  MuiDrawer: {
    styleOverrides: { paper: { borderRight: 'none' } },
  },
  MuiAppBar: {
    styleOverrides: { root: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)' } },
  },
  MuiChip: {
    styleOverrides: { root: { borderRadius: 3, height: 24 } },
  },
  MuiTooltip: {
    styleOverrides: { tooltip: { fontSize: '0.6875rem', backgroundColor: '#333', borderRadius: 2 } },
  },
  MuiMenu: {
    styleOverrides: { paper: { borderRadius: 4, boxShadow: '0 2px 10px rgba(0,0,0,0.12)' } },
  },
};

// Zoho Mail exact color palette - Light theme
const getLightThemeOptions = (accentColor: AccentColor = 'blue'): ThemeOptions => ({
  palette: {
    mode: 'light',
    primary: getPrimaryColor(accentColor),
    secondary: {
      main: '#f3f4f6',
      light: '#f9fafb',
      dark: '#e5e7eb',
      contrastText: '#374151',
    },
    background: { default: '#f5f5f5', paper: '#ffffff' },
    text: { primary: '#333333', secondary: '#666666' },
    divider: '#e0e0e0',
    error: { main: '#d32f2f' },
    warning: { main: '#ffa000' },
    success: { main: '#388e3c' },
    info: { main: '#1976d2' },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: sharedLightComponents,
});

// Dark theme options
const getDarkThemeOptions = (accentColor: AccentColor = 'blue'): ThemeOptions => {
  const primaryColor = getPrimaryColor(accentColor);
  return {
    palette: {
      mode: 'dark',
      primary: {
        main: primaryColor.light, // Use light variant as main for better visibility
        light: primaryColor.main,
        dark: primaryColor.dark,
        contrastText: '#000',
      },
      secondary: {
        main: '#374151',
        light: '#4b5563',
        dark: '#1f2937',
        contrastText: '#fff',
      },
      background: { default: '#111827', paper: '#1f2937' },
      text: { primary: '#f3f4f6', secondary: '#9ca3af' },
      divider: '#374151',
      error: { main: '#ef4444' },
      warning: { main: '#f59e0b' },
      success: { main: '#10b981' },
      info: { main: '#60a5fa' },
    },
    typography: sharedTypography,
    shape: sharedShape,
    components: {
      ...sharedLightComponents,
      MuiPaper: {
        styleOverrides: {
          root: { 
            backgroundImage: 'none', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            backgroundColor: '#1f2937',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: '#1f2937',
            backgroundImage: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: '#1f2937',
            backgroundImage: 'none',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: '#1f2937',
            backgroundImage: 'none',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: '#1f2937',
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 500 },
          contained: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.4)' },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: 'rgba(96, 165, 250, 0.2)',
              '&:hover': { backgroundColor: 'rgba(96, 165, 250, 0.3)' },
            },
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundImage: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
        },
      },
      MuiDrawer: {
        styleOverrides: { paper: { backgroundImage: 'none', backgroundColor: '#111827' } },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#1f2937',
              '& fieldset': { borderColor: '#374151' },
              '&:hover fieldset': { borderColor: '#4b5563' },
              '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
            },
            '& .MuiInputLabel-root': { color: '#9ca3af' },
            '& .MuiInputBase-input': { color: '#f3f4f6' },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: { backgroundColor: '#1f2937' },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { borderColor: '#374151' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { 
            color: '#9ca3af',
            '&.Mui-selected': { color: '#60a5fa' },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: '#374151' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { 
            backgroundColor: '#374151',
            color: '#f3f4f6',
            borderColor: '#4b5563',
          },
          outlined: {
            borderColor: '#4b5563',
          },
        },
      },
    },
  };
};

// Create theme with accent color and palette support
export const createAppTheme = (mode: 'light' | 'dark', accentColor: AccentColor = 'blue', paletteColors?: { sidebar: string; content: string }): Theme => {
  console.log('🎨 createAppTheme called with mode:', mode, 'accentColor:', accentColor, 'paletteColors:', paletteColors);
  
  if (mode === 'dark') {
    const darkOptions = getDarkThemeOptions(accentColor);
    if (paletteColors) {
      // Apply specific palette colors to the dark theme
      // background.default = main content area (use content color)
      // background.paper = cards, dialogs, papers (lighter than content for contrast)
      const contentColor = paletteColors.content;
      const paperColor = lightenColor(contentColor, 0.05); // Slightly lighter for cards
      
      darkOptions.palette = {
        ...darkOptions.palette,
        background: {
          default: contentColor,    // Main content area
          paper: paperColor,         // Cards, dialogs, papers
        },
      };
    }
    return createTheme(darkOptions);
  }
  
  const lightOptions = getLightThemeOptions(accentColor);
  if (paletteColors) {
    // Apply specific palette colors to the light theme
    lightOptions.palette = {
      ...lightOptions.palette,
      background: {
        default: paletteColors.content, // Main content area
        paper: '#ffffff',                // Keep paper white for light themes
      },
    };
  }
  return createTheme(lightOptions);
};

// Helper to lighten a color (for paper in dark themes)
function lightenColor(color: string, amount: number): string {
  // Simple hex color lightening
  const hex = color.replace('#', '');
  const rgb = parseInt(hex, 16);
  const r = Math.min(255, ((rgb >> 16) & 0xff) + Math.floor(255 * amount));
  const g = Math.min(255, ((rgb >> 8) & 0xff) + Math.floor(255 * amount));
  const b = Math.min(255, (rgb & 0xff) + Math.floor(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// Default themes for backward compatibility
export const lightTheme = createTheme(getLightThemeOptions('blue'));
export const darkTheme = createTheme(getDarkThemeOptions('blue'));

export default lightTheme;
